import {
    Node as SomatNode,
    SequenceType,
    InputType,
    Survey,
    SurveySession,
    Indicator, Paragraph, OptionGroup, IndicatorResponse, UploadedFile, SurveySubmission, SessionEvaluation, Campaign
} from "@/types/models";
import axios, {AxiosResponse} from "axios";
import {EvaluationChange} from "@/Context/EvaluationChangesContext";

export type ResponseUpdate = {
    indicator_id: number
    node_index?: number | null
    input?: string | null
    input_text?: string | null
    assumed_from_response_id?: number | undefined
    evaluation?: boolean
}

export const getSurveys = async() => {
    return await axios
        .get(route("api.survey.index"))
        .then((response: AxiosResponse<Survey[]>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const getInputTypes = async(session: SurveySession) => {
    return await axios
        .get(route("api.session.input-types", {
            session: session.id,
        }))
        .then((response: AxiosResponse<InputType[]>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const getSurveySequenceTypes = async (session: SurveySession) => {
    return await axios
        .get(route("api.session.sequence-types", {session: session}))
        .then((response: AxiosResponse<SequenceType[]>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const getSurveyNodeContent = async (session: SurveySession, node: SomatNode, nodeIndex?: number | undefined): Promise<SomatNode> => {
    return await axios
        .get(route("api.session.node-content", {
            session: session.id,
            node: node.id,
            nodeIndex: nodeIndex,
        }))
        .then((response: AxiosResponse<SomatNode>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const getOptionGroups = async(session: SurveySession) => {
    return await axios
        .get(route("api.session.option-groups", {
            session: session.id,
        }))
        .then((response: AxiosResponse<OptionGroup[]>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const postResponse = async(session: SurveySession, response: ResponseUpdate) => {
    return await axios
        .post(route("api.session.respond"), {
            survey_session_id: session.id,
            ...response
        })
        .then((response: AxiosResponse<IndicatorResponse>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const postUploadedFileDescription = async(file: UploadedFile, description: string) => {
    return await axios
        .patch(route("api.session.update-file-description"), {
            id: file.id,
            description: description
        })
        .then((response: AxiosResponse<UploadedFile>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}
export const postFileResponseInfo = async(responseUploadId: number, info: {reference?: string, comments?: string}) => {
    return await axios
        .patch(route("update-respond-upload-info"), {
            ...info,
            id: responseUploadId,
        })
        .then((response: AxiosResponse<UploadedFile>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}


export const getUploadedFiles = async(response: IndicatorResponse) => {
    return await axios
        .get(route("api.session.uploads", {
            response: response.id,
        }))
        .then((res: AxiosResponse<UploadedFile[]>) => {
            response.uploadedFiles = res.data
            return res.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const postSubmitSession = async(session: SurveySession, comments?: string, force?: boolean) => {
    const params: { survey_session_id: number, comments?: string, force?: boolean } = {
        survey_session_id: session.id,
    }
    if(comments) params.comments = comments
    if(force) params.force = true
    return await axios
        .post(route("api.session.submit"), params)
        .then((response: AxiosResponse<SurveySubmission>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}

export const postSubmitEvaluation = async(evaluation: SessionEvaluation) => {
    return await axios
        .post(route("api.session.submit-evaluation"), {...evaluation, id: undefined})
        .then((response: AxiosResponse<SurveySubmission>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })

}

/**
 * Every answer in a session that a review replaced, so the form can mark them without asking one by one.
 */
export const getEvaluationChanges = async(session: SurveySession) => {
    return await axios
        .get(route("api.session.evaluation-changes", {session: session.id}))
        .then((response: AxiosResponse<EvaluationChange[]>) => {
            return response.data ?? []
        })
        .catch((error: Error) => {
            console.warn(`getEvaluationChanges call failed: ${error}`)
            throw error
        })
}

export const getOriginalResponse = async(session: SurveySession, indicator: Indicator, nodeIndex?: number) => {
    const params: {session: number, indicator: number, nodeIndex?: number } = {
        session: session.id,
        indicator: indicator.id,
    }
    if(nodeIndex) params.nodeIndex = nodeIndex
    return await axios
        .get(route("api.session.original-response", params))
        .then((response: AxiosResponse<IndicatorResponse | null>) => {
            return response.data
        })
        .catch((error: Error) => {
            console.warn(`${route} call failed: ${error}`)
            throw error
        })
}
